/**
 * Server-Sent Events over fetch, so the stream can carry an Authorization header (EventSource cannot).
 * The parser is incremental and pure; `connectSse` adds the network loop with backoff.
 */

export interface SseMessage {
  event: string;
  data: string;
  id: string | null;
}

export interface SseParser {
  /** Feed a decoded text chunk; returns the complete messages it finished. */
  push(chunk: string): SseMessage[];
  /** Last `retry:` value in ms, if the server sent one. */
  readonly retry: number | null;
  readonly lastEventId: string | null;
}

/** Implements the WHATWG event-stream parsing rules (fields, comments, CR/LF/CRLF, multi-line data). */
export function createSseParser(): SseParser {
  let buffer = '';
  let data: string[] = [];
  let event = '';
  let hasData = false;
  let lastEventId: string | null = null;
  let retry: number | null = null;
  let pendingCR = false;

  const dispatch = (out: SseMessage[]): void => {
    if (hasData) out.push({ event: event || 'message', data: data.join('\n'), id: lastEventId });
    data = [];
    event = '';
    hasData = false;
  };

  const processLine = (line: string, out: SseMessage[]): void => {
    if (line === '') return dispatch(out);
    if (line.startsWith(':')) return;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    switch (field) {
      case 'data':
        data.push(value);
        hasData = true;
        break;
      case 'event':
        event = value;
        break;
      case 'id':
        if (!value.includes('\0')) lastEventId = value;
        break;
      case 'retry':
        if (/^\d+$/.test(value)) retry = Number(value);
        break;
    }
  };

  return {
    push(chunk: string): SseMessage[] {
      const out: SseMessage[] = [];
      let text = chunk;
      // A CR at the end of the previous chunk may be the first half of a CRLF.
      if (pendingCR && text.startsWith('\n')) text = text.slice(1);
      pendingCR = false;
      buffer += text;
      let start = 0;
      for (let i = 0; i < buffer.length; i++) {
        const ch = buffer[i];
        if (ch === '\n' || ch === '\r') {
          processLine(buffer.slice(start, i), out);
          if (ch === '\r') {
            if (i + 1 < buffer.length) {
              if (buffer[i + 1] === '\n') i++;
            } else {
              pendingCR = true;
            }
          }
          start = i + 1;
        }
      }
      buffer = buffer.slice(start);
      return out;
    },
    get retry() {
      return retry;
    },
    get lastEventId() {
      return lastEventId;
    },
  };
}

/** Exponential backoff with full jitter, capped. `random` is injectable for tests. */
export function backoffDelay(attempt: number, baseMs = 1000, maxMs = 30_000, random: () => number = Math.random) {
  const ceiling = Math.min(maxMs, baseMs * 2 ** Math.max(0, attempt));
  return Math.round(ceiling / 2 + (random() * ceiling) / 2);
}

export type SseStatus = 'connecting' | 'open' | 'retrying' | 'closed';

export interface ConnectSseOptions {
  url: string;
  getToken: () => Promise<string>;
  onMessage: (message: SseMessage) => void;
  onStatus?: (status: SseStatus) => void;
  fetchFn?: (input: string, init?: RequestInit) => Promise<Response>;
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
}

const abortableSleep = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    const t = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(t);
        resolve();
      },
      { once: true },
    );
  });

/** Keeps a stream open until `close()`; reconnects with backoff and resumes with Last-Event-ID. */
export function connectSse(options: ConnectSseOptions): { close: () => void } {
  const controller = new AbortController();
  const { signal } = controller;
  const fetchFn = options.fetchFn ?? ((i, init) => fetch(i, init));
  const sleep = options.sleep ?? abortableSleep;
  const status = (s: SseStatus): void => options.onStatus?.(s);

  const run = async (): Promise<void> => {
    let attempt = 0;
    let lastEventId: string | null = null;
    let serverRetry: number | null = null;
    while (!signal.aborted) {
      status(attempt === 0 ? 'connecting' : 'retrying');
      try {
        const token = await options.getToken();
        const headers: Record<string, string> = { Accept: 'text/event-stream', Authorization: `Bearer ${token}` };
        if (lastEventId) headers['Last-Event-ID'] = lastEventId;
        const res = await fetchFn(options.url, { headers, signal, credentials: 'omit', cache: 'no-store' });
        if (!res.ok || !res.body) throw new Error(`events stream failed: ${res.status}`);
        status('open');
        attempt = 0;
        const parser = createSseParser();
        const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          for (const msg of parser.push(value)) options.onMessage(msg);
          lastEventId = parser.lastEventId ?? lastEventId;
          serverRetry = parser.retry ?? serverRetry;
        }
      } catch {
        if (signal.aborted) break;
      }
      if (signal.aborted) break;
      await sleep(serverRetry ?? backoffDelay(attempt), signal);
      attempt++;
    }
    status('closed');
  };
  void run();
  return { close: () => controller.abort() };
}
